import React, { useEffect, useRef, useState } from 'react'
import { HiX, HiOutlineFilm, HiCheckCircle } from 'react-icons/hi'
import { compressVideo, formatBytes, formatSeconds, inspectVideo, MAX_VIDEO_SECONDS } from '../util/videoFile'

// Prepares a product video in the browser: checks it (portrait, up to 60 s), compresses it to the shop's MP4 format
// with a cover frame, and shows the result before it is used. Same dialog shell as ImageCropper.
// onDone({ file (video/mp4 File), poster (image/jpeg File), url, duration }); onCancel(message?) — a message when the
// video can't be used.
const VideoUploader = ({ file, onCancel, onDone }) => {
    const [stage, setStage] = useState('checking') // checking | compressing | ready
    const [progress, setProgress] = useState(0)
    const [result, setResult] = useState(null)
    const dialogRef = useRef(null)
    const cancelRef = useRef(onCancel)
    cancelRef.current = onCancel
    const abortRef = useRef(null)

    useEffect(() => {
        const abort = new AbortController()
        abortRef.current = abort
        let previewUrl = null
        inspectVideo(file)
            .then(() => {
                if (abort.signal.aborted) return null
                setStage('compressing')
                return compressVideo(file, p => setProgress(p), abort.signal)
            })
            .then(done => {
                if (!done || abort.signal.aborted) return
                previewUrl = URL.createObjectURL(done.blob)
                setResult({ ...done, url: previewUrl })
                setStage('ready')
            })
            .catch(error => {
                if (abort.signal.aborted || error.name === 'AbortError') return
                cancelRef.current(error.message)
            })
        return () => {
            abort.abort()
            if (previewUrl) URL.revokeObjectURL(previewUrl)
        }
    }, [file])

    // lock page scroll (unless something else already did), Escape cancels, focus the dialog
    useEffect(() => {
        const root = document.documentElement
        const lockedBefore = root.classList.contains('mv-scroll-locked')
        if (!lockedBefore) root.classList.add('mv-scroll-locked')
        const returnFocus = document.activeElement
        const onKey = (e) => { if (e.key === 'Escape') cancelRef.current() }
        document.addEventListener('keydown', onKey)
        if (dialogRef.current) dialogRef.current.focus({ preventScroll: true })
        return () => {
            if (!lockedBefore) root.classList.remove('mv-scroll-locked')
            document.removeEventListener('keydown', onKey)
            if (returnFocus && returnFocus.focus && document.contains(returnFocus)) returnFocus.focus({ preventScroll: true })
        }
    }, [])

    const use = () => {
        const base = (file.name || 'video').replace(/\.[^.]+$/, '') || 'video'
        onDone({
            file: new File([result.blob], base + '.mp4', { type: 'video/mp4' }),
            poster: new File([result.poster], base + '-cover.jpg', { type: 'image/jpeg' }),
            url: result.url,
            duration: result.duration,
        })
    }

    const percent = Math.round(progress * 100)

    return (
        <div className='mv-cropper-overlay'>
            <div className='mv-cropper mv-video-uploader' role='dialog' aria-modal='true' aria-labelledby='mv-video-title'
                tabIndex={-1} ref={dialogRef}>
                <header className='mv-cropper-head'>
                    <div>
                        <h2 id='mv-video-title' className='mv-cropper-title'>Product video</h2>
                        <p className='mv-cropper-sub'>Portrait · up to {MAX_VIDEO_SECONDS} seconds · shown on phones</p>
                    </div>
                    <button type='button' className='mv-icon-btn mv-cropper-close' aria-label='Cancel' onClick={() => onCancel()}>
                        <HiX />
                    </button>
                </header>

                <div className='mv-video-stage'>
                    {stage === 'ready' && result ?
                        <video className='mv-video-preview' src={result.url} controls playsInline autoPlay muted loop /> :
                        <div className='mv-video-working' aria-live='polite'>
                            <HiOutlineFilm className='mv-video-working-icon' aria-hidden='true' />
                            <p className='mv-video-working-title'>
                                {stage === 'checking' ? 'Checking the video…' : `Preparing the video… ${percent}%`}
                            </p>
                            <div className='mv-video-progress' role='progressbar' aria-label='Preparing the video'
                                aria-valuemin={0} aria-valuemax={100} aria-valuenow={stage === 'checking' ? 0 : percent}>
                                <span style={{ width: (stage === 'checking' ? 4 : Math.max(4, percent)) + '%' }} />
                            </div>
                            <p className='mv-video-working-note'>
                                Making it light and sharp for phones. Keep this page open; it takes about as long as the video.
                            </p>
                        </div>}
                </div>

                {stage === 'ready' && result &&
                    <p className='mv-video-summary' role='status'>
                        <HiCheckCircle aria-hidden='true' />
                        <span>
                            Ready · {formatSeconds(result.duration)} · {result.width}×{result.height} · <strong>{formatBytes(result.blob.size)}</strong>
                            {file.size > result.blob.size ? ` (was ${formatBytes(file.size)})` : ''}
                        </span>
                    </p>}

                <footer className='mv-cropper-foot'>
                    <button type='button' className='mv-btn mv-btn-secondary' onClick={() => onCancel()}>Cancel</button>
                    <button type='button' className='mv-btn mv-btn-primary' onClick={use} disabled={stage !== 'ready'}>
                        Use video
                    </button>
                </footer>
            </div>
        </div>
    )
}

export default VideoUploader
